// SHADOW NETWORK — Top 5 traders side widget (additive).
// Lives next to the feed on /shadow. Pulls /api/ranking/top5 on mount
// and links to the full ranking page. Position emojis match the leaderboard.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trophy, Clock, ShoppingBag } from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";

const TIER_EMOJI = { 1: "👑", 2: "🥷", 3: "⚡", 4: "🎯", 5: "🔥" };
const TIER_COLOR = { 1: "#fbbf24", 2: "#c8d3e8", 3: "#facc15", 4: "#fb923c", 5: "#f97316" };

export const RankingWidget = () => {
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/ranking/top5`);
        const json = await safeJson(res);
        if (res.ok && json) setData(json);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const top5 = data?.top5 || [];
  const daysLeft = data?.days_left ?? "—";

  return (
    <aside
      className="rounded-2xl border border-white/[0.07] bg-zinc-950/55 p-4 backdrop-blur-md"
      data-testid="ranking-widget"
    >
      <header className="mb-3 flex items-center justify-between">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-200">
          {ui.rw_top_traders}
        </p>
      </header>

      <div className="space-y-px overflow-hidden rounded-lg border border-white/[0.05] bg-black/30">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-[11px] text-white/40">
            <Loader2 className="h-3 w-3 animate-spin" /> {ui.rw_calculating}
          </div>
        ) : top5.length === 0 ? (
          <p className="px-3 py-5 text-center text-[10px] text-white/30">
            {ui.rw_empty}
          </p>
        ) : (
          top5.map((row) => (
            <div
              key={`${row.user_id}-${row.rank}`}
              className="flex items-center gap-2 border-b border-white/[0.04] px-3 py-2 last:border-b-0"
              data-testid={`ranking-widget-row-${row.rank}`}
            >
              <span className="text-sm leading-none" aria-hidden>
                {TIER_EMOJI[row.rank] || "🚀"}
              </span>
              <span className="font-mono text-[9px] font-bold" style={{ color: TIER_COLOR[row.rank] || "#a78bfa" }}>
                #{row.rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-white/85">
                {row.user_name}
              </span>
              <span className="font-mono text-[11px] font-black text-cyan-200">
                {Math.round(row.total_score)}
              </span>
            </div>
          ))
        )}
      </div>

      <Link
        to="/shadow/ranking"
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 transition-colors hover:border-cyan-400/40 hover:text-cyan-200"
        data-testid="ranking-widget-cta"
      >
        <Trophy className="h-3 w-3" /> {ui.rw_see_full}
      </Link>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[9px]">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
          <p className="inline-flex items-center gap-1 text-amber-200">
            <Clock className="h-2.5 w-2.5" /> {daysLeft} {ui.rw_days_unit}
          </p>
          <p className="text-white/35">{ui.rw_until_month_end}</p>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
          <p className="inline-flex items-center gap-1 text-cyan-200">
            <ShoppingBag className="h-2.5 w-2.5" /> {ui.rw_prize}
          </p>
          <p className="text-white/35">{ui.rw_prize_value}</p>
        </div>
      </div>
    </aside>
  );
};

export default RankingWidget;
